import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  ALIGN_TYPE,
  Button,
  FONT_VARIANT_TYPE,
  ModalDialog,
  SpinnerOverlayView,
  TextBlock,
} from '@/components/Elements';
import { FormInput } from '@/components/Form';
import { API_STATUS } from '@/constants';

import {
  SERIAL_LINK_FORM_SCHEMA,
  SERIAL_LINK_VALIDATE,
  SerialLinkForm,
} from '../constants';
import {
  SuccessFunction,
  useSerialLinkDialog,
} from '../hooks/useSerialLinkDialog';

import styles from './SerialLinkDialog.module.scss';

/**
 * プロパティ
 */
type SerialLinkDialog = {
  // ダイアログ表示/非表示(true=表示)
  isOpen: boolean;
  // ダイアログを閉じる
  onClose: () => void;
  // シリアル連携の成功時に実行したい処理
  onSerialLinkSuccessFun: SuccessFunction;
};

/**
 * シリアル連携ダイアログ コンポーネント
 */
export const SerialLinkDialog = React.memo(
  ({ isOpen, onClose, onSerialLinkSuccessFun }: SerialLinkDialog) => {
    const {
      onClickSerialRegUpdate,
      serialLinkErrorMessage,
      serialAuthState,
      resetSerialLinkStatusType,
      defaultSerial,
      serialLinkDialogContent,
    } = useSerialLinkDialog({ onSuccessFun: onSerialLinkSuccessFun });
    const { t } = useTranslation();

    // フォーム設定
    const {
      register,
      handleSubmit,
      formState: { errors },
      setFocus,
      setValue,
      reset,
    } = useForm<SerialLinkForm>({
      resolver: zodResolver(SERIAL_LINK_FORM_SCHEMA),
    });

    // 自動フォーカスとデフォルト値設定
    useEffect(() => {
      if (isOpen) {
        setFocus('serialLink');
        setValue('serialLink', defaultSerial);
      }
    }, [defaultSerial, isOpen, setFocus, setValue]);

    // ダイアログが閉じられた時にフォームをリセット
    useEffect(() => {
      if (!isOpen) {
        reset();
        resetSerialLinkStatusType();
      }
    }, [isOpen, reset, resetSerialLinkStatusType]);

    return (
      <>
        <SpinnerOverlayView
          isOpen={serialAuthState.status === API_STATUS.LOADING}
        />
        <ModalDialog
          isOpen={isOpen}
          isHiddenCloseButton={false}
          onClickClose={onClose}
          title={t(
            `serialLinkDialog.title.${serialLinkDialogContent.title}`,
          ).toString()}
        >
          <div className={styles['serial-link-container']}>
            <form onSubmit={handleSubmit(onClickSerialRegUpdate)}>
              <div className={styles['serial-link-text']}>
                <p>
                  {t(
                    `serialLinkDialog.${serialLinkDialogContent.text}`,
                  ).toString()}
                </p>
              </div>

              <div className={styles['serial-link-form-section']}>
                <div className={styles['serial-link-form-title']}>
                  <TextBlock text={t('serial.シリアルコード')} />
                </div>
                <FormInput
                  type="text"
                  id="serialLink"
                  name="serialLink"
                  placeholder="AAAAA-1111-2222-3333-4444"
                  errorMessage={
                    errors.serialLink
                      ? t(`serial.${errors.serialLink.message}`).toString()
                      : undefined
                  }
                  maxLength={SERIAL_LINK_VALIDATE.LENGTH.MAX}
                  registration={register('serialLink')}
                />
              </div>

              <div className={`${styles['serial-link-button']}`}>
                <Button
                  title={t(
                    `serialLinkDialog.buttonName.${serialLinkDialogContent.buttonName}`,
                  ).toString()}
                  type="submit"
                  disabled={
                    errors.serialLink !== undefined ||
                    serialAuthState.status === API_STATUS.LOADING
                  }
                  gtmClassTag={serialLinkDialogContent.buttonTagName}
                />
              </div>

              <div className={styles['serial-link-form-error']}>
                <TextBlock
                  text={
                    serialLinkErrorMessage
                      ? t(
                          `serialLinkDialog.${serialLinkErrorMessage}`,
                        ).toString()
                      : ''
                  }
                  align={ALIGN_TYPE.center}
                  fontVariant={FONT_VARIANT_TYPE.alert}
                />
              </div>
            </form>
          </div>
        </ModalDialog>
      </>
    );
  },
);
